import React, { useMemo, useState } from 'react'
import { Plus, DotsThreeVertical } from '@phosphor-icons/react'
import { Trash2, Edit2 } from 'react-feather'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { putRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import FormTile from 'src/components/common/containers/FormTile'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useSubscriptionPermissions } from 'src/lib/access-permission-hooks/useSubscriptionPermissions'
import PaymentChargeForm from './PaymentChargeForm'
import { useLocale } from 'src/lib/hooks/useLocal'
import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@shopflo/ui'
import GenericTable from 'src/components/common/table/GenericTable'
import { ColumnDef } from '@tanstack/react-table'
import { PaymentMode, AdditionalChargeType, PaymentModeScope } from 'src/lib/types/paymentSettings'
import { getAdditionalChargePayload, buildPaymentModePayload } from 'src/lib/utils/payment-page'

const EXCLUDED_PAYMENT_MODES = [
  'GYFTR',
  'SHOPIFY_GC',
  'FREE',
  'CF',
  'SIMPL',
  'SIMPL_PL',
  'GIFTCARD',
  'BHARATX',
  'BAJAJ_INTEGRATION',
  'CUSTOM_PAYMENT',
  'GC',
  'GIFTCARD',
  'REWARDS_WALLET',
  'LOYALTY'
] as const

const scopeOf = (mode: { purchase_type?: PaymentModeScope }): PaymentModeScope => mode.purchase_type ?? 'CHECKOUT'

const SCOPES: PaymentModeScope[] = ['CHECKOUT', 'SUBSCRIPTION']

const SCOPE_LABEL_KEY: Record<PaymentModeScope, string> = {
  CHECKOUT: 'payment_additional_charges.scope_checkout',
  SUBSCRIPTION: 'payment_additional_charges.scope_subscription'
}

const PaymentAdditionalCharges: React.FC = () => {
  const { t } = useLocale()
  const { setLoaderProperties } = useLoader()
  const [selectedMode, setSelectedMode] = useState<string | null>(null)
  const [selectedScope, setSelectedScope] = useState<PaymentModeScope>('CHECKOUT')
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isEditingMode, setIsEditingMode] = useState<boolean>(false)

  const {
    state: { paymentModes: contextPaymentModes },
    actions: { mutatePaymentModes }
  } = useMerchant()

  const { canViewSubscriptions } = useSubscriptionPermissions()

  /* Subscription charges belong to the subscriptions tab, so they follow its permission. */
  const visibleScopes = useMemo(
    () => SCOPES.filter(scope => scope !== 'SUBSCRIPTION' || canViewSubscriptions),
    [canViewSubscriptions]
  )

  const getFilteredPaymentModes = useMemo(() => {
    if (!contextPaymentModes) return []

    return contextPaymentModes.filter(mode => {
      const modeCode = mode.mode
      return !EXCLUDED_PAYMENT_MODES.some(excluded => excluded === modeCode)
    })
  }, [contextPaymentModes])

  const getModeData = (mode: string): PaymentMode => {
    const modeFromContext = getFilteredPaymentModes.find(
      paymentMode => paymentMode.mode === mode && scopeOf(paymentMode) === selectedScope
    )
    if (!modeFromContext) {
      throw new Error(`Payment mode ${mode} not found`)
    }

    return modeFromContext as PaymentMode
  }

  const getModesForScope = (scope: PaymentModeScope) => getFilteredPaymentModes.filter(mode => scopeOf(mode) === scope)

  const getModesWithCharges = (scope: PaymentModeScope): PaymentMode[] =>
    getModesForScope(scope).filter(mode => mode?.metadata?.additionalChargeValue) as PaymentMode[]

  const handleOpenModal = (scope: PaymentModeScope) => {
    const modesForScope = getModesForScope(scope)

    setSelectedScope(scope)
    setIsModalOpen(true)
    setIsEditingMode(false)
    setSelectedMode(modesForScope.length > 0 ? modesForScope[0].mode : null)
  }

  const handleEditMode = (modeToEdit: string, scope: PaymentModeScope) => {
    setIsEditingMode(true)
    setSelectedScope(scope)
    setSelectedMode(modeToEdit)
    setIsModalOpen(true)
  }

  const handleRemoveFromList = async (modeToRemove: string, scope: PaymentModeScope) => {
    try {
      setLoaderProperties({
        isLoaderVisible: true,
        message: t('payment_additional_charges.removing_additional_charge')
      })

      if (!contextPaymentModes?.length) {
        errorToast(t('payment_additional_charges.payment_modes_data_not_available'))
        return
      }

      const modesPayload: PaymentMode[] = contextPaymentModes.map(mode => {
        const chargePayload = getAdditionalChargePayload(mode, modeToRemove, scope)
        return buildPaymentModePayload(mode, chargePayload)
      })

      await putRequest('/fms/v1/merchant-gateways/reorder-payment-modes', apiURI.FLO_CHECKOUT, { modes: modesPayload })

      mutatePaymentModes()

      successToast(t('payment_additional_charges.additional_charge_removed_successfully'))
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        t('payment_additional_charges.failed_to_remove_additional_charge')
      errorToast(errorMessage)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const addChargesButton = (scope: PaymentModeScope) => (
    <Button onClick={() => handleOpenModal(scope)} leftIcon={Plus} size='md' variant='outline'>
      <span>{t('payment_additional_charges.add_additional_charges')}</span>
    </Button>
  )

  const buildColumns = (scope: PaymentModeScope): ColumnDef<PaymentMode>[] => [
    {
      accessorKey: 'title',
      header: t('payment_additional_charges.payment_method'),
      cell: ({ row }) => (
        <div className='text-text-sm-medium text-carbon'>
          {row.original.metadata?.title || row.original.title || row.original.mode}
        </div>
      )
    },
    {
      accessorKey: 'metadata.additionalChargeType',
      header: t('payment_additional_charges.charge_type'),
      cell: ({ row }) => (
        <span className='text-sm text-carbon'>
          {row.original.metadata?.additionalChargeType === AdditionalChargeType.PERCENTAGE
            ? t('payment_additional_charges.percentage')
            : t('payment_additional_charges.flat')}
        </span>
      )
    },
    {
      accessorKey: 'metadata.additionalChargeValue',
      header: t('payment_additional_charges.charge_value'),
      cell: ({ row }) => (
        <div className='text-center'>
          <span className='text-text-sm-medium text-carbon'>
            {row.original.metadata?.additionalChargeType === AdditionalChargeType.PERCENTAGE
              ? `${row.original.metadata?.additionalChargeValue}%`
              : `₹${row.original.metadata?.additionalChargeValue}`}
          </span>
        </div>
      )
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className='flex items-center justify-end'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                IconComponent={DotsThreeVertical}
                size='sm'
                variant='ghost'
                onClick={e => e.stopPropagation()}
                className='text-gray-600'
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-10 p-1 bg-white border border-gray-100'>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleEditMode(row.original.mode, scope)
                }}
                className='cursor-pointer px-3 py-2.5 text-text-sm-regular text-carbon hover:bg-gray-100 hover:text-carbon focus:bg-gray-100 focus:text-carbon rounded flex items-center gap-2.5 outline-none'
              >
                <Edit2 size={14} className='text-gray-600' />
                <span>{t('payment_additional_charges.edit')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleRemoveFromList(row.original.mode, scope)
                }}
                className='cursor-pointer px-3 py-2.5 text-text-sm-regular text-carbon hover:bg-gray-100 hover:text-carbon focus:bg-gray-100 focus:text-carbon rounded flex items-center gap-2.5 outline-none'
              >
                <Trash2 size={14} className='text-red-500' />
                <span>{t('payment_additional_charges.delete')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  ]

  if (!contextPaymentModes || contextPaymentModes.length === 0) {
    return (
      <div className='flex flex-col space-y-3'>
        <div className='h-24 bg-gray-100 rounded-lg animate-pulse' />
        <div className='h-24 bg-gray-100 rounded-lg animate-pulse' />
        <div className='h-24 bg-gray-100 rounded-lg animate-pulse' />
      </div>
    )
  }

  return (
    <>
      <FormTile className='pt-3'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-0.5'>
            <h3 className='text-text-md-medium text-carbon'>{t('payment_additional_charges.title')}</h3>
            <p className='text-text-sm-regular text-gray-dark'>{t('payment_additional_charges.description')}</p>
          </div>

          {visibleScopes.map(scope => {
            const modesWithCharges = getModesWithCharges(scope)
            const hasCharges = modesWithCharges.length > 0

            if (!getModesForScope(scope).length) return null

            return (
              <div key={scope} className='flex flex-col gap-1.5'>
                <h3 className='text-text-sm-semibold text-zinc-600'>{t(SCOPE_LABEL_KEY[scope])}</h3>

                <div className='max-h-[300px] overflow-y-auto'>
                  <GenericTable
                    data={modesWithCharges}
                    columns={buildColumns(scope)}
                    isCustomizable={{
                      hideHeaders: !hasCharges,
                      applyBorder: true,
                      cellClassName: 'p-2',
                      headerClassName: 'p-2 text-text-sm-semibold',
                      hidePaginationButton: true
                    }}
                    customPaginationConfig={{
                      customPagination: true
                    }}
                    emptyStateAction={addChargesButton(scope)}
                    tableFooterComp={{
                      shouldShowTableFooter: hasCharges,
                      tableFooter: <div className='flex p-2 pb-4'>{addChargesButton(scope)}</div>
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </FormTile>

      <PaymentChargeForm
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        availableModes={getModesForScope(selectedScope)}
        selectedMode={selectedMode}
        scope={selectedScope}
        onModeChange={setSelectedMode}
        isEditMode={isEditingMode}
        getModeData={getModeData}
      />
    </>
  )
}

export default PaymentAdditionalCharges
